import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { TrendingUp, ChevronLeft, ChevronRight } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";

const PALETTE = ["#2F6B45", "#1B4383", "#A87F31", "#6D4AA6", "#C24E4E", "#2C8BB0", "#94A3B8"];
const WINDOW = 6;

const monthShort = (m) => {
  const [y, mm] = (m || "").split("-");
  const names = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${names[parseInt(mm, 10)] || m} '${(y || "").slice(2)}`;
};

/**
 * Monthly trend line chart. Fetches the full history and shows a fixed WINDOW-month
 * viewing frame with ◀/▶ navigation so you can page as far back as data exists.
 */
export default function TrendPanel({ title, subtitleFor, endpoint, metrics, defaultCurrency = "USD", testid = "trend-panel" }) {
  const mlist = metrics && metrics.length ? metrics : null;
  const [currency, setCurrency] = useState(defaultCurrency);
  const [metric, setMetric] = useState(mlist ? mlist[0].key : "");
  const [trend, setTrend] = useState(null);
  const [end, setEnd] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams({ currency, full: "true" });
    if (metric) params.set("metric", metric);
    api.get(`${endpoint}?${params.toString()}`).then((r) => { setTrend(r.data); setEnd((r.data?.data || []).length); }).catch(() => {});
  }, [endpoint, currency, metric]);

  const curOptions = trend?.currencies || ["USD", "TZS"];
  const series = trend?.series || [];
  const allRows = trend?.data || [];
  const endIdx = end == null ? allRows.length : Math.min(end, allRows.length);
  const startIdx = Math.max(0, endIdx - WINDOW);
  const view = allRows.slice(startIdx, endIdx).map((d) => ({ ...d, month: monthShort(d.month) }));
  const hasData = allRows.some((d) => series.some((s) => (d[s.label] || 0) > 0));
  const canPrev = startIdx > 0;
  const canNext = endIdx < allRows.length;
  const subtitle = subtitleFor ? subtitleFor(currency, metric) : `Monthly value (${currency})`;

  return (
    <div className="mb-6 rounded-xl border border-border bg-white p-5" data-testid={testid}>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-[#E6ECF5] text-[#1B4383]"><TrendingUp className="h-4 w-4" /></span>
          <div>
            <div className="font-heading font-semibold text-slate-900">{title}</div>
            <div className="text-xs text-muted-foreground">{subtitle}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-0.5">
            <button onClick={() => setEnd((e) => Math.max(WINDOW, (e == null ? allRows.length : e) - 1))} disabled={!canPrev} className="rounded p-1 text-slate-500 hover:bg-slate-100 disabled:opacity-30" title="Older months" data-testid={`${testid}-prev`}><ChevronLeft className="h-4 w-4" /></button>
            <button onClick={() => setEnd((e) => Math.min(allRows.length, (e == null ? allRows.length : e) + 1))} disabled={!canNext} className="rounded p-1 text-slate-500 hover:bg-slate-100 disabled:opacity-30" title="Newer months" data-testid={`${testid}-next`}><ChevronRight className="h-4 w-4" /></button>
          </div>
          {mlist && mlist.length > 1 && (
            <div className="flex rounded-md border border-border p-0.5">
              {mlist.map((m) => (
                <button key={m.key} onClick={() => setMetric(m.key)} data-testid={`${testid}-metric-${m.key}`}
                  className={`rounded px-2.5 py-1 text-xs font-medium ${metric === m.key ? "bg-brand text-white" : "text-muted-foreground hover:text-slate-900"}`}>
                  {m.label}
                </button>
              ))}
            </div>
          )}
          {curOptions.length > 1 && (
            <div className="flex rounded-md border border-border p-0.5">
              {curOptions.map((c) => (
                <button key={c} onClick={() => setCurrency(c)} data-testid={`${testid}-cur-${c}`}
                  className={`rounded px-2.5 py-1 text-xs font-medium ${currency === c ? "bg-brand text-white" : "text-muted-foreground hover:text-slate-900"}`}>
                  {c}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      {!hasData ? (
        <div className="rounded-lg bg-slate-50 py-10 text-center text-sm text-muted-foreground">
          Not enough history yet — activity will chart here month by month.
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={view} margin={{ top: 5, right: 20, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} width={56}
              tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
            <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 12 }}
              formatter={(v) => `${currency} ${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            {series.map((s, idx) => (
              <Line key={s.value} type="monotone" dataKey={s.label} stroke={PALETTE[idx % PALETTE.length]}
                strokeWidth={2.5} dot={{ r: 2 }} activeDot={{ r: 5 }} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
